<template>
    <div class="accommodations-editor">
        <!-- Empty State -->
        <div v-if="!modelValue || modelValue.length === 0" class="empty-state congress-schedule-empty-state">
            <p class="mb-4">
                {{ lang('congresses::congresses.accommodations.empty') }}
            </p>
            <Button type="button" size="sm" color="brand" variant="inline" @click="addAccommodation">
                <span class="action-icon">
                    <Icon icon="phosphor/regular/building-apartment"></Icon>
                </span>
                <span class="action-label-text">
                    {{ lang('congresses::congresses.accommodations.add_first') }}
                </span>
            </Button>
        </div>

        <!-- Accommodations List -->
        <div v-else class="accommodations-list">
            <Sortable :list="modelValue" item-key="_id" tag="div" @end="onReordered">
                <template #item="{ element: accommodation, index }">
                    <div :key="`${accommodation._id}-${index}`" class="tree-line-item grid grid-cols-12">
                        <div class="col-span-1">
                            <TreeLine />
                        </div>
                        <div class="col-span-11">
                            <AccommodationCard
                                :key="accommodation._id"
                                v-model="modelValue[index]"
                                :index="index"
                                :available-tickets="availableTickets"
                                @remove="removeAccommodation(index)"
                            />
                        </div>
                    </div>
                </template>
            </Sortable>

            <!-- Add Button -->
            <div class="congress-schedule-after-event-list-actions">
                <Button type="button" size="sm" color="brand" variant="inline" @click="addAccommodation">
                    <span class="action-icon">
                        <Icon icon="phosphor/regular/building-apartment"></Icon>
                    </span>
                    <span class="action-label-text">
                        {{ lang('congresses::congresses.accommodations.add') }}
                    </span>
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { usePage } from '@inertiajs/vue3';
import { lang } from '@/lang/composable';
import Sortable from '@src/Common/Components/Sortable.vue';
import Button from '@src/Common/Components/Button.vue';
import TreeLine from '@src/Common/Components/TreeLine.vue';
import AccommodationCard from './AccommodationCard.vue';
import type { Accommodation } from '../types';

interface Ticket {
    id: number;
    name: string;
}

const availableTickets = computed<Ticket[]>(() => {
    const page = usePage();
    return (page.props.object as any)?.tickets || [];
});

const modelValue = defineModel<Accommodation[]>();

// Ensure all items have _id
const ensureIds = (accommodations: Accommodation[]) => {
    return accommodations.map((acc) => ({
        ...acc,
        _id: acc._id || uuidv4(),
        ticket_ids: acc.ticket_ids || acc.tickets || [],
        images: (acc.images || []).map((img) => ({
            ...img,
            _id: img._id || uuidv4(),
        })),
        rooms: (acc.rooms || []).map((room) => ({
            ...room,
            _id: room._id || uuidv4(),
            ticket_ids: room.ticket_ids || room.tickets || [],
        })),
    }));
};

if (!modelValue.value) {
    modelValue.value = [];
} else {
    modelValue.value = ensureIds(modelValue.value);
}

const addAccommodation = () => {
    modelValue.value = [
        ...modelValue.value,
        {
            _id: uuidv4(),
            name: '',
            is_display_only: false,
            stars: 3,
            description: '',
            info: {},
            images: [],
            rooms: [],
            sort_order: modelValue.value.length,
        },
    ];
};

const removeAccommodation = (index: number) => {
    if (confirm(lang('congresses::congresses.accommodations.confirm_remove'))) {
        modelValue.value = modelValue.value.filter((_, idx) => idx !== index);
    }
};

const onReordered = (event: { oldIndex: number; newIndex: number }) => {
    const items = [...modelValue.value];
    const [movedItem] = items.splice(event.oldIndex, 1);
    items.splice(event.newIndex, 0, movedItem);

    items.forEach((acc, idx) => {
        acc.sort_order = idx;
    });

    modelValue.value = items;
};
</script>
